import { useState, useEffect } from 'react'
import { colorFromUserId, initialsFromName } from '@/lib/utils/avatar'
import { fetchMediaUrl } from '@/lib/utils/decrypt-attachment'

interface AvatarProps {
  url?: string | null
  mxc?: string | null
  name: string
  userId?: string
  size?: number
  className?: string
  /** Square (rounded) instead of circular — used in the Slack-style UI. */
  square?: boolean
}

const avatarCache = new Map<string, string>()

function useAvatarUrl(mxc: string | null | undefined): string | null {
  const [blobUrl, setBlobUrl] = useState<string | null>(() =>
    mxc ? avatarCache.get(mxc) ?? null : null
  )

  useEffect(() => {
    if (!mxc) return
    const cached = avatarCache.get(mxc)
    if (cached) {
      setBlobUrl(cached)
      return
    }
    fetchMediaUrl(mxc)
      .then((url) => {
        avatarCache.set(mxc, url)
        setBlobUrl(url)
      })
      .catch(() => setBlobUrl(null))
  }, [mxc])

  return blobUrl
}

export function Avatar({ url, mxc, name, userId, size = 40, className = '', square = false }: AvatarProps) {
  const bgColor = colorFromUserId(userId ?? name)
  const initials = initialsFromName(name)
  const fetchedUrl = useAvatarUrl(mxc)
  const imgSrc = fetchedUrl ?? url
  const shape = square ? 'rounded-lg' : 'rounded-full'

  if (imgSrc) {
    return (
      <img
        src={imgSrc}
        alt={name}
        width={size}
        height={size}
        className={`${shape} object-cover shrink-0 bg-[rgb(var(--np-raised))] ${className}`}
        style={{ width: size, height: size, minWidth: size, minHeight: size }}
      />
    )
  }

  return (
    <div
      className={`${shape} flex items-center justify-center text-white font-semibold shrink-0 ${className}`}
      style={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        backgroundColor: bgColor,
        fontSize: size * 0.38,
      }}
    >
      {initials}
    </div>
  )
}
